import { useTranslation } from 'react-i18next'
import { Upload, X, Image, Camera } from 'lucide-react'
import type { UploadedImage } from './ImageUploadGrid'

interface ImageSlotCellProps {
  img: UploadedImage | null
  index: number
  inputRef: (el: HTMLInputElement | null) => void
  onRemove: (index: number) => void
  onFileChange: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void
}

export function ImageSlotCell({ img, index, inputRef, onRemove, onFileChange }: ImageSlotCellProps) {
  const { t } = useTranslation()
  const isMobile = 'ontouchstart' in window
  return (
    <div
      className="relative aspect-square rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-600 flex items-center justify-center overflow-hidden cursor-pointer hover:border-orange-400 transition-colors"
      onClick={() => !img && document.getElementById(`img-input-${index}`)?.click()}
    >
      {img ? (
        <>
          <img src={img.previewUrl} alt="" className="w-full h-full object-cover" />
          {img.uploading && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(index) }}
            className="absolute top-1 right-1 p-0.5 bg-black/60 rounded-full hover:bg-black/80"
          >
            <X className="h-3 w-3 text-white" />
          </button>
          {index === 0 && (
            <span className="absolute bottom-0 left-0 right-0 text-center text-2xs bg-orange-500 text-white py-0.5">
              {t('amazonListing.manual.mainImage')}
            </span>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center text-neutral-400">
          {index === 0 ? <Image className="h-5 w-5 mb-1" /> : <Upload className="h-4 w-4 mb-1" />}
          <span className="text-2xs">{index === 0 ? t('amazonListing.manual.mainImage') : `${index + 1}`}</span>
          {isMobile && (
            <button
              onClick={(e) => { e.stopPropagation(); document.getElementById(`img-camera-${index}`)?.click() }}
              className="mt-1 flex items-center gap-1 text-2xs text-primary-500"
            >
              <Camera className="h-3 w-3" />
              {t('amazonListing.manual.takePhoto')}
            </button>
          )}
        </div>
      )}
      <input
        id={`img-input-${index}`}
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => onFileChange(index, e)}
      />
      {isMobile && (
        <input
          id={`img-camera-${index}`}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={(e) => onFileChange(index, e)}
        />
      )}
    </div>
  )
}
